import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../services/supabase'
import {
  RefreshCw,
  Layers,
  ShieldCheck,
  AlertCircle,
  Check,
  X,
  Mail,
  Save,
  Send,
  Eye,
} from 'lucide-react'
import { useAdminAuth } from '../contexts/AdminAuthContext'
import {
  DEFAULT_EMAIL_TEXTS,
  buildTemplatePreview,
  type EmailTemplateKind,
} from '../utils/emailTemplates'

interface RecursoConfig {
  id: string
  chave: string
  nome: string
  descricao: string
  acesso_trial: boolean
  acesso_start: boolean
  acesso_medium: boolean
  acesso_pro: boolean
  bloquear_no_trial: boolean
  ativo_geral: boolean
  ordenacao: number
}

interface ConfigEmail {
  id?: string
  ativo: boolean
  provedor: 'smtp' | 'resend'
  from_nome: string
  from_email: string
  smtp_host: string
  smtp_port: number
  smtp_secure: boolean
  smtp_user: string
  smtp_pass: string
  resend_api_key: string
  enviar_ativacao: boolean
  enviar_pagamento: boolean
  ativacao_assunto: string
  ativacao_titulo: string
  ativacao_corpo: string
  pagamento_assunto: string
  pagamento_titulo: string
  pagamento_corpo: string
}

const emptyEmailConfig: ConfigEmail = {
  ativo: false,
  provedor: 'smtp',
  from_nome: 'Latera',
  from_email: 'noreply@tabindex.com.br',
  smtp_host: '',
  smtp_port: 587,
  smtp_secure: false,
  smtp_user: '',
  smtp_pass: '',
  resend_api_key: '',
  enviar_ativacao: true,
  enviar_pagamento: true,
  ativacao_assunto: DEFAULT_EMAIL_TEXTS.ativacao.assunto,
  ativacao_titulo: DEFAULT_EMAIL_TEXTS.ativacao.titulo,
  ativacao_corpo: DEFAULT_EMAIL_TEXTS.ativacao.corpo,
  pagamento_assunto: DEFAULT_EMAIL_TEXTS.pagamento.assunto,
  pagamento_titulo: DEFAULT_EMAIL_TEXTS.pagamento.titulo,
  pagamento_corpo: DEFAULT_EMAIL_TEXTS.pagamento.corpo,
}

const PASS_MASK = '••••••••'

export default function AdminConfiguracoes() {
  const { admin } = useAdminAuth()
  const [aba, setAba] = useState<'recursos' | 'email' | 'mensagens'>('recursos')
  const [recursos, setRecursos] = useState<RecursoConfig[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<{ id: string; field: string } | null>(null)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const [emailConfig, setEmailConfig] = useState<ConfigEmail>(emptyEmailConfig)
  const [emailLoading, setEmailLoading] = useState(false)
  const [emailSaving, setEmailSaving] = useState(false)
  const [smtpPassTouched, setSmtpPassTouched] = useState(false)
  const [resendKeyTouched, setResendKeyTouched] = useState(false)
  const [hadSmtpPass, setHadSmtpPass] = useState(false)
  const [hadResendKey, setHadResendKey] = useState(false)
  const [testEmailTo, setTestEmailTo] = useState('')
  const [testSending, setTestSending] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; text: string } | null>(null)
  const [diagLoading, setDiagLoading] = useState(false)
  const [diagResult, setDiagResult] = useState<Record<string, unknown> | null>(null)
  const [previewKind, setPreviewKind] = useState<EmailTemplateKind>('pagamento')
  const [textosDisponiveis, setTextosDisponiveis] = useState(true)

  const loadConfig = async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('config_recursos')
        .select('*')
        .order('ordenacao', { ascending: true })

      if (error) throw error
      setRecursos(data || [])
    } catch (error: any) {
      console.error('Erro ao carregar configurações:', error)
      setMessage({
        type: 'error',
        text: `Erro: ${(error as any).message || 'Não foi possível carregar as configurações.'}`,
      })
    } finally {
      setLoading(false)
    }
  }

  const loadEmailConfig = async () => {
    setEmailLoading(true)
    try {
      const { data, error } = await supabase
        .from('config_email')
        .select('*')
        .limit(1)
        .maybeSingle()

      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          setMessage({
            type: 'error',
            text: 'Tabela config_email não existe. Execute sql/criar_config_email.sql no Supabase.',
          })
          return
        }
        throw error
      }

      if (data) {
        const hasPass = Boolean(data.smtp_pass)
        const hasKey = Boolean(data.resend_api_key)
        const hasTextCols = 'ativacao_assunto' in data
        setTextosDisponiveis(hasTextCols)
        setHadSmtpPass(hasPass)
        setHadResendKey(hasKey)
        setSmtpPassTouched(false)
        setResendKeyTouched(false)
        setEmailConfig({
          id: data.id,
          ativo: Boolean(data.ativo),
          provedor: data.provedor === 'resend' ? 'resend' : 'smtp',
          from_nome: data.from_nome || 'Latera',
          from_email: data.from_email || 'noreply@tabindex.com.br',
          smtp_host: data.smtp_host || '',
          smtp_port: Number(data.smtp_port || 587),
          smtp_secure: Boolean(data.smtp_secure),
          smtp_user: data.smtp_user || '',
          smtp_pass: hasPass ? PASS_MASK : '',
          resend_api_key: hasKey ? PASS_MASK : '',
          enviar_ativacao: data.enviar_ativacao !== false,
          enviar_pagamento: data.enviar_pagamento !== false,
          ativacao_assunto: data.ativacao_assunto || DEFAULT_EMAIL_TEXTS.ativacao.assunto,
          ativacao_titulo: data.ativacao_titulo || DEFAULT_EMAIL_TEXTS.ativacao.titulo,
          ativacao_corpo: data.ativacao_corpo || DEFAULT_EMAIL_TEXTS.ativacao.corpo,
          pagamento_assunto: data.pagamento_assunto || DEFAULT_EMAIL_TEXTS.pagamento.assunto,
          pagamento_titulo: data.pagamento_titulo || DEFAULT_EMAIL_TEXTS.pagamento.titulo,
          pagamento_corpo: data.pagamento_corpo || DEFAULT_EMAIL_TEXTS.pagamento.corpo,
        })
      } else {
        setEmailConfig(emptyEmailConfig)
        setTextosDisponiveis(true)
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: `Erro ao carregar e-mail: ${error.message}` })
    } finally {
      setEmailLoading(false)
    }
  }

  useEffect(() => {
    void loadConfig()
  }, [])

  useEffect(() => {
    if (aba === 'email' || aba === 'mensagens') void loadEmailConfig()
  }, [aba])

  useEffect(() => {
    if (admin?.email && !testEmailTo) {
      setTestEmailTo(admin.email)
    }
  }, [admin?.email, testEmailTo])

  const preview = useMemo(() => {
    const texts =
      previewKind === 'ativacao'
        ? {
            assunto: emailConfig.ativacao_assunto,
            titulo: emailConfig.ativacao_titulo,
            corpo: emailConfig.ativacao_corpo,
          }
        : {
            assunto: emailConfig.pagamento_assunto,
            titulo: emailConfig.pagamento_titulo,
            corpo: emailConfig.pagamento_corpo,
          }
    return buildTemplatePreview(previewKind, texts)
  }, [previewKind, emailConfig])

  const handleTogglePermission = async (
    id: string,
    field: keyof RecursoConfig,
    currentValue: boolean
  ) => {
    setSaving({ id, field })
    setMessage(null)

    try {
      const updates: any = { [field]: !currentValue }

      if (field === 'acesso_trial') {
        updates.bloquear_no_trial = currentValue
      }

      const { error } = await supabase.from('config_recursos').update(updates).eq('id', id)

      if (error) throw error

      setRecursos(
        recursos.map((r) =>
          r.id === id
            ? {
                ...r,
                [field]: !currentValue,
                ...(field === 'acesso_trial' ? { bloquear_no_trial: currentValue } : {}),
              }
            : r
        )
      )

      setMessage({ type: 'success', text: 'Permissão atualizada!' })
      setTimeout(() => setMessage(null), 2000)
    } catch (error: any) {
      console.error('Erro ao salvar:', error)
      setMessage({ type: 'error', text: `Erro ao salvar: ${error.message}` })
    } finally {
      setSaving(null)
    }
  }

  const saveEmailConfig = async () => {
    setEmailSaving(true)
    setMessage(null)
    try {
      const payload: Record<string, unknown> = {
        ativo: emailConfig.ativo,
        provedor: emailConfig.provedor,
        from_nome: emailConfig.from_nome.trim(),
        from_email: emailConfig.from_email.trim(),
        smtp_host: emailConfig.smtp_host.trim() || null,
        smtp_port: Number(emailConfig.smtp_port) || 587,
        smtp_secure: emailConfig.smtp_secure,
        smtp_user: emailConfig.smtp_user.trim() || null,
        enviar_ativacao: emailConfig.enviar_ativacao,
        enviar_pagamento: emailConfig.enviar_pagamento,
        updated_at: new Date().toISOString(),
      }

      if (textosDisponiveis) {
        payload.ativacao_assunto = emailConfig.ativacao_assunto.trim()
        payload.ativacao_titulo = emailConfig.ativacao_titulo.trim()
        payload.ativacao_corpo = emailConfig.ativacao_corpo.trim()
        payload.pagamento_assunto = emailConfig.pagamento_assunto.trim()
        payload.pagamento_titulo = emailConfig.pagamento_titulo.trim()
        payload.pagamento_corpo = emailConfig.pagamento_corpo.trim()
      }

      if (smtpPassTouched && emailConfig.smtp_pass && emailConfig.smtp_pass !== PASS_MASK) {
        payload.smtp_pass = emailConfig.smtp_pass
      } else if (!hadSmtpPass && emailConfig.smtp_pass && emailConfig.smtp_pass !== PASS_MASK) {
        payload.smtp_pass = emailConfig.smtp_pass
      }

      if (
        resendKeyTouched &&
        emailConfig.resend_api_key &&
        emailConfig.resend_api_key !== PASS_MASK
      ) {
        payload.resend_api_key = emailConfig.resend_api_key
      } else if (
        !hadResendKey &&
        emailConfig.resend_api_key &&
        emailConfig.resend_api_key !== PASS_MASK
      ) {
        payload.resend_api_key = emailConfig.resend_api_key
      }

      let error
      if (emailConfig.id) {
        ;({ error } = await supabase.from('config_email').update(payload).eq('id', emailConfig.id))
      } else {
        const insert = await supabase.from('config_email').insert(payload).select('id').single()
        error = insert.error
        if (insert.data?.id) {
          setEmailConfig((c) => ({ ...c, id: insert.data.id }))
        }
      }

      if (error) throw error

      // Invalida cache do backend (best-effort)
      try {
        const backendUrl =
          import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
          import.meta.env.VITE_BACKEND_PIX_URL ||
          ''
        if (backendUrl) {
          await fetch(`${backendUrl}/api/pix-auto/notificacoes/invalidate-cache`, {
            method: 'POST',
          })
        }
      } catch {
        /* ignore */
      }

      setMessage({ type: 'success', text: 'Configuração de e-mail salva!' })
      setSmtpPassTouched(false)
      setResendKeyTouched(false)
      await loadEmailConfig()
      setTimeout(() => setMessage(null), 2500)
    } catch (error: any) {
      setMessage({ type: 'error', text: `Erro ao salvar e-mail: ${error.message}` })
    } finally {
      setEmailSaving(false)
    }
  }

  const sendTestEmail = async () => {
    setTestSending(true)
    setTestResult(null)
    setMessage(null)
    try {
      if (!testEmailTo.trim() || !testEmailTo.includes('@')) {
        throw new Error('Informe um e-mail válido para o teste')
      }

      // Salva antes se houver alterações óbvias de senha não salvas — avisa o usuário
      const backendUrl =
        import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
        import.meta.env.VITE_BACKEND_PIX_URL ||
        ''
      if (!backendUrl) {
        throw new Error('VITE_BACKEND_PIX_AUTO_URL não configurada')
      }

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Sessão admin expirada. Faça login novamente.')
      }

      const controller = new AbortController()
      const timeoutId = window.setTimeout(() => controller.abort(), 35000)

      let res: Response
      try {
        res = await fetch(`${backendUrl}/api/pix-auto/notificacoes/teste-email`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ to: testEmailTo.trim() }),
          signal: controller.signal,
        })
      } catch (fetchErr: any) {
        if (fetchErr?.name === 'AbortError') {
          throw new Error(
            'Tempo esgotado aguardando o servidor. O Railway pode estar bloqueado para conectar no SMTP (587/465).'
          )
        }
        throw fetchErr
      } finally {
        window.clearTimeout(timeoutId)
      }

      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data.ok) {
        throw new Error(data.error || `Falha no teste (HTTP ${res.status})`)
      }

      setTestResult({
        ok: true,
        text: `E-mail de teste enviado para ${data.to || testEmailTo} via ${String(data.provedor || emailConfig.provedor).toUpperCase()}${data.port ? ` (porta ${data.port})` : ''}. Confira a caixa de entrada (e spam).`,
      })
      setMessage({ type: 'success', text: 'Teste de e-mail enviado com sucesso!' })
    } catch (error: any) {
      const text = error?.message || 'Falha ao enviar teste'
      setTestResult({ ok: false, text })
      setMessage({ type: 'error', text })
    } finally {
      setTestSending(false)
    }
  }

  const runSmtpDiagnostico = async () => {
    setDiagLoading(true)
    setDiagResult(null)
    setMessage(null)
    try {
      const backendUrl =
        import.meta.env.VITE_BACKEND_PIX_AUTO_URL ||
        import.meta.env.VITE_BACKEND_PIX_URL ||
        ''
      if (!backendUrl) throw new Error('VITE_BACKEND_PIX_AUTO_URL não configurada')

      const { data: { session } } = await supabase.auth.getSession()
      if (!session?.access_token) {
        throw new Error('Sessão admin expirada. Faça login novamente.')
      }

      const host = encodeURIComponent(emailConfig.smtp_host || 'mail.tabindex.com.br')
      const res = await fetch(
        `${backendUrl}/api/pix-auto/notificacoes/smtp-diagnostico?host=${host}`,
        {
          headers: { Authorization: `Bearer ${session.access_token}` },
        }
      )
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`)
      setDiagResult(data)
      if (data.railwayOutboundIp) {
        setMessage({
          type: 'success',
          text: `IP de saída do Railway: ${data.railwayOutboundIp}. Libere esse IP no firewall do e-mail.`,
        })
      }
    } catch (error: any) {
      setMessage({ type: 'error', text: error?.message || 'Falha no diagnóstico' })
    } finally {
      setDiagLoading(false)
    }
  }

  const PermissionSwitch = ({
    checked,
    onChange,
    disabled,
    colorClass,
  }: {
    checked: boolean
    onChange: () => void
    disabled: boolean
    colorClass: string
  }) => (
    <button
      onClick={onChange}
      disabled={disabled}
      className={`
                relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
                ${checked ? colorClass : 'bg-gray-200 dark:bg-gray-700'}
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
            `}
    >
      <span className="sr-only">Alternar permissão</span>
      <span
        className={`
                    pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${checked ? 'translate-x-4' : 'translate-x-0'}
                `}
      />
    </button>
  )

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            {aba === 'email' || aba === 'mensagens' ? (
              <Mail className="w-8 h-8 text-indigo-600" />
            ) : (
              <Layers className="w-8 h-8 text-indigo-600" />
            )}
            Configurações
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {aba === 'email'
              ? 'SMTP / Resend para disparos de e-mail de assinatura e pagamento.'
              : aba === 'mensagens'
                ? 'Edite os textos e veja a prévia dos e-mails enviados aos clientes.'
                : 'Gerencie exatamente o que cada plano pode acessar no sistema.'}
          </p>
        </div>

        <button
          onClick={() =>
            aba === 'recursos' ? void loadConfig() : void loadEmailConfig()
          }
          className="p-2 text-gray-500 hover:text-indigo-600 dark:text-gray-400 dark:hover:text-indigo-400 transition-colors"
          title="Recarregar"
        >
          <RefreshCw
            className={`w-5 h-5 ${loading || emailLoading ? 'animate-spin' : ''}`}
          />
        </button>
      </div>

      <div className="mb-6 flex gap-2 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setAba('recursos')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            aba === 'recursos'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Recursos & Planos
        </button>
        <button
          onClick={() => setAba('email')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            aba === 'email'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          E-mail (SMTP)
        </button>
        <button
          onClick={() => setAba('mensagens')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
            aba === 'mensagens'
              ? 'border-indigo-600 text-indigo-600'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          Mensagens / Templates
        </button>
      </div>

      {message && (
        <div
          className={`mb-6 p-4 rounded-lg flex items-center gap-3 ${
            message.type === 'success'
              ? 'bg-green-50 text-green-700 border border-green-200 dark:bg-green-900/20 dark:border-green-800'
              : 'bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:border-red-800'
          }`}
        >
          {message.type === 'error' && <AlertCircle className="w-5 h-5 flex-shrink-0" />}
          {message.type === 'success' && <ShieldCheck className="w-5 h-5 flex-shrink-0" />}
          <p>{message.text}</p>
        </div>
      )}

      {aba === 'mensagens' ? (
        <div className="space-y-4">
          {!textosDisponiveis && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Para salvar os textos no banco, execute{' '}
              <code className="font-mono text-xs">sql/adicionar_textos_emails.sql</code> no
              Supabase. A prévia abaixo já funciona com os textos padrão.
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setPreviewKind('pagamento')}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                previewKind === 'pagamento'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              Pagamento confirmado
            </button>
            <button
              type="button"
              onClick={() => setPreviewKind('ativacao')}
              className={`rounded-lg px-3 py-2 text-sm font-medium ${
                previewKind === 'ativacao'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-200'
              }`}
            >
              Assinatura ativada
            </button>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Textos —{' '}
                {previewKind === 'pagamento' ? 'Pagamento confirmado' : 'Assinatura ativada'}
              </h2>

              {previewKind === 'pagamento' ? (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Assunto</label>
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.pagamento_assunto}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, pagamento_assunto: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Título (cabeçalho)
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.pagamento_titulo}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, pagamento_titulo: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Corpo (use linha em branco para novo parágrafo)
                    </label>
                    <textarea
                      rows={6}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.pagamento_corpo}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, pagamento_corpo: e.target.value }))
                      }
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Assunto</label>
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.ativacao_assunto}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, ativacao_assunto: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Título (cabeçalho)
                    </label>
                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.ativacao_titulo}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, ativacao_titulo: e.target.value }))
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Corpo (use linha em branco para novo parágrafo)
                    </label>
                    <textarea
                      rows={6}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.ativacao_corpo}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, ativacao_corpo: e.target.value }))
                      }
                    />
                  </div>
                </>
              )}

              <button
                type="button"
                onClick={() => void saveEmailConfig()}
                disabled={emailSaving || !textosDisponiveis}
                className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {emailSaving ? 'Salvando...' : 'Salvar textos'}
              </button>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-3">
              <div className="flex items-center gap-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                <Eye className="w-4 h-4 text-indigo-600" />
                Prévia
              </div>
              <div className="text-xs text-gray-500">
                Assunto: <span className="font-medium text-gray-800 dark:text-gray-200">{preview.assunto}</span>
              </div>
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50">
                <iframe
                  title="Prévia do e-mail"
                  className="w-full h-[520px] bg-white"
                  srcDoc={preview.html}
                  sandbox=""
                />
              </div>
              <p className="text-xs text-gray-500">
                Dados de exemplo: plano, valor e datas fictícios — só para visualizar o layout.
              </p>
            </div>
          </div>
        </div>
      ) : aba === 'email' ? (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 space-y-6">
          {emailLoading ? (
            <div className="py-12 text-center text-gray-500">Carregando...</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-4">
                <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-800 dark:text-gray-200">
                  <input
                    type="checkbox"
                    checked={emailConfig.ativo}
                    onChange={(e) =>
                      setEmailConfig((c) => ({ ...c, ativo: e.target.checked }))
                    }
                    className="rounded border-gray-300"
                  />
                  Envio de e-mails ativo
                </label>
                <select
                  value={emailConfig.provedor}
                  onChange={(e) =>
                    setEmailConfig((c) => ({
                      ...c,
                      provedor: e.target.value as 'smtp' | 'resend',
                    }))
                  }
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                >
                  <option value="smtp">SMTP</option>
                  <option value="resend">Resend (API)</option>
                </select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Nome do remetente
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                    value={emailConfig.from_nome}
                    onChange={(e) =>
                      setEmailConfig((c) => ({ ...c, from_nome: e.target.value }))
                    }
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    E-mail remetente
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                    value={emailConfig.from_email}
                    onChange={(e) =>
                      setEmailConfig((c) => ({ ...c, from_email: e.target.value }))
                    }
                  />
                </div>
              </div>

              {emailConfig.provedor === 'smtp' ? (
                <div className="space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                    Servidor SMTP
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-gray-500 mb-1">Host</label>
                      <input
                        placeholder="smtp.seudominio.com"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                        value={emailConfig.smtp_host}
                        onChange={(e) =>
                          setEmailConfig((c) => ({ ...c, smtp_host: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Porta</label>
                      <input
                        type="number"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                        value={emailConfig.smtp_port}
                        onChange={(e) =>
                          setEmailConfig((c) => ({
                            ...c,
                            smtp_port: Number(e.target.value) || 587,
                          }))
                        }
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Usuário</label>
                      <input
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                        value={emailConfig.smtp_user}
                        onChange={(e) =>
                          setEmailConfig((c) => ({ ...c, smtp_user: e.target.value }))
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Senha</label>
                      <input
                        type="password"
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                        value={emailConfig.smtp_pass}
                        onChange={(e) => {
                          setSmtpPassTouched(true)
                          setEmailConfig((c) => ({ ...c, smtp_pass: e.target.value }))
                        }}
                        placeholder={hadSmtpPass ? 'Deixe mascarado para manter' : ''}
                      />
                    </div>
                  </div>
                  <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                    <input
                      type="checkbox"
                      checked={emailConfig.smtp_secure}
                      onChange={(e) =>
                        setEmailConfig((c) => ({ ...c, smtp_secure: e.target.checked }))
                      }
                      className="rounded border-gray-300"
                    />
                    Conexão segura (SSL/TLS — porta 465)
                  </label>
                </div>
              ) : (
                <div className="space-y-4 border-t border-gray-100 dark:border-gray-700 pt-4">
                  <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">Resend</p>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">API Key</label>
                    <input
                      type="password"
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                      value={emailConfig.resend_api_key}
                      onChange={(e) => {
                        setResendKeyTouched(true)
                        setEmailConfig((c) => ({ ...c, resend_api_key: e.target.value }))
                      }}
                      placeholder={hadResendKey ? 'Deixe mascarado para manter' : 're_...'}
                    />
                  </div>
                </div>
              )}

              <div className="space-y-3 border-t border-gray-100 dark:border-gray-700 pt-4">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Quais e-mails disparar
                </p>
                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-gray-300"
                    checked={emailConfig.enviar_pagamento}
                    onChange={(e) =>
                      setEmailConfig((c) => ({ ...c, enviar_pagamento: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>Pagamento confirmado</strong> — quando a fatura é paga (valor, plano,
                    obrigado pelo pagamento)
                  </span>
                </label>
                <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                  <input
                    type="checkbox"
                    className="mt-1 rounded border-gray-300"
                    checked={emailConfig.enviar_ativacao}
                    onChange={(e) =>
                      setEmailConfig((c) => ({ ...c, enviar_ativacao: e.target.checked }))
                    }
                  />
                  <span>
                    <strong>Assinatura ativada</strong> — boas-vindas na primeira ativação (com
                    próximo vencimento)
                  </span>
                </label>
              </div>

              <div className="space-y-3 border-t border-gray-100 dark:border-gray-700 pt-4">
                <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                  Testar envio
                </p>
                <p className="text-xs text-gray-500">
                  Salve antes de testar. Porta <strong>587</strong> ou <strong>465</strong>. O teste
                  sai do <strong>IP do Railway</strong> (não do seu PC). Se der timeout, libere esse
                  IP no firewall do e-mail — use o botão de diagnóstico abaixo.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-900"
                    value={testEmailTo}
                    onChange={(e) => setTestEmailTo(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => void sendTestEmail()}
                    disabled={testSending}
                    className="inline-flex items-center justify-center gap-2 rounded-lg border border-indigo-300 bg-indigo-50 px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 dark:border-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300"
                  >
                    <Send className={`w-4 h-4 ${testSending ? 'animate-pulse' : ''}`} />
                    {testSending ? 'Enviando...' : 'Enviar e-mail de teste'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => void runSmtpDiagnostico()}
                  disabled={diagLoading}
                  className="text-sm text-indigo-600 hover:underline disabled:opacity-50"
                >
                  {diagLoading ? 'Diagnosticando...' : 'Ver IP do Railway e testar portas 587/465'}
                </button>
                {diagResult && (
                  <pre className="overflow-x-auto rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-800 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200">
                    {JSON.stringify(diagResult, null, 2)}
                  </pre>
                )}
                {testResult && (
                  <div
                    className={`rounded-lg border px-3 py-2 text-sm ${
                      testResult.ok
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-red-200 bg-red-50 text-red-800'
                    }`}
                  >
                    {testResult.text}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <button
                  onClick={() => void saveEmailConfig()}
                  disabled={emailSaving}
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {emailSaving ? 'Salvando...' : 'Salvar configuração'}
                </button>
              </div>
            </>
          )}
        </div>
      ) : (
        <>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 text-sm font-medium text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/50">
                    <th className="px-6 py-4 w-1/3">Recurso / Funcionalidade</th>
                    <th className="px-4 py-4 text-center w-32 bg-orange-50/50 dark:bg-orange-900/10 border-l border-gray-100 dark:border-gray-700">
                      <div className="flex flex-col items-center">
                        <span className="font-bold text-orange-600 dark:text-orange-400">TRIAL</span>
                        <span className="text-xs font-normal opacity-75">7 dias</span>
                      </div>
                    </th>
                    <th className="px-4 py-4 text-center w-32 border-l border-gray-100 dark:border-gray-700">
                      <div className="flex flex-col items-center">
                        <span className="font-bold text-gray-700 dark:text-gray-300">START</span>
                        <span className="text-xs font-normal opacity-75">Básico</span>
                      </div>
                    </th>
                    <th className="px-4 py-4 text-center w-32 bg-blue-50/50 dark:bg-blue-900/10 border-l border-gray-100 dark:border-gray-700">
                      <div className="flex flex-col items-center">
                        <span className="font-bold text-blue-600 dark:text-blue-400">MEDIUM</span>
                        <span className="text-xs font-normal opacity-75">Intermediário</span>
                      </div>
                    </th>
                    <th className="px-4 py-4 text-center w-32 bg-purple-50/50 dark:bg-purple-900/10 border-l border-gray-100 dark:border-gray-700">
                      <div className="flex flex-col items-center">
                        <span className="font-bold text-purple-600 dark:text-purple-400">PRO</span>
                        <span className="text-xs font-normal opacity-75">Completo</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                        <p className="mt-4 text-gray-500">Carregando permissões...</p>
                      </td>
                    </tr>
                  ) : (
                    recursos.map((recurso) => (
                      <tr
                        key={recurso.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-2 h-2 rounded-full ${
                                saving?.id === recurso.id
                                  ? 'bg-yellow-400 animate-pulse'
                                  : 'bg-green-500'
                              }`}
                            ></div>
                            <div>
                              <span className="font-medium text-gray-900 dark:text-gray-100 block">
                                {recurso.nome}
                              </span>
                              <span className="text-xs text-gray-500 dark:text-gray-400">
                                {recurso.descricao}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center border-l border-gray-100 dark:border-gray-700 bg-orange-50/30 dark:bg-orange-900/5">
                          <div className="flex flex-col items-center gap-1">
                            <PermissionSwitch
                              checked={recurso.acesso_trial}
                              onChange={() =>
                                handleTogglePermission(
                                  recurso.id,
                                  'acesso_trial',
                                  recurso.acesso_trial
                                )
                              }
                              disabled={saving?.id === recurso.id}
                              colorClass="bg-orange-500"
                            />
                            <span className="text-[10px] text-gray-400 font-medium">
                              {recurso.acesso_trial ? 'LIBERADO' : 'BLOQUEADO'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center border-l border-gray-100 dark:border-gray-700">
                          <PermissionSwitch
                            checked={recurso.acesso_start}
                            onChange={() =>
                              handleTogglePermission(
                                recurso.id,
                                'acesso_start',
                                recurso.acesso_start
                              )
                            }
                            disabled={saving?.id === recurso.id}
                            colorClass="bg-gray-600"
                          />
                        </td>
                        <td className="px-4 py-4 text-center border-l border-gray-100 dark:border-gray-700 bg-blue-50/30 dark:bg-blue-900/5">
                          <PermissionSwitch
                            checked={recurso.acesso_medium}
                            onChange={() =>
                              handleTogglePermission(
                                recurso.id,
                                'acesso_medium',
                                recurso.acesso_medium
                              )
                            }
                            disabled={saving?.id === recurso.id}
                            colorClass="bg-blue-600"
                          />
                        </td>
                        <td className="px-4 py-4 text-center border-l border-gray-100 dark:border-gray-700 bg-purple-50/30 dark:bg-purple-900/5">
                          <PermissionSwitch
                            checked={recurso.acesso_pro}
                            onChange={() =>
                              handleTogglePermission(recurso.id, 'acesso_pro', recurso.acesso_pro)
                            }
                            disabled={saving?.id === recurso.id}
                            colorClass="bg-purple-600"
                          />
                        </td>
                      </tr>
                    ))
                  )}

                  {!loading && recursos.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                        Nenhum recurso encontrado no banco de dados.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="mt-6 flex flex-col md:flex-row gap-4 text-sm text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="flex-1">
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Controle de Acesso Flexível
              </p>
              <p>
                Esta matriz controla em tempo real o que cada usuário pode acessar. As alterações
                são aplicadas imediatamente.
              </p>
            </div>
            <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 dark:border-gray-700 pt-4 md:pt-0 md:pl-4">
              <p className="font-semibold text-gray-700 dark:text-gray-300 mb-2">Legenda:</p>
              <ul className="space-y-1">
                <li className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  <span>Recurso Liberado para o plano</span>
                </li>
                <li className="flex items-center gap-2">
                  <X className="w-4 h-4 text-red-600" />
                  <span>Recurso Bloqueado (usuário vê tela de upgrade)</span>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
